import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { StorageKeys } from '@core/enums';
import { API, environment } from '@core/envs';
import { IDealerByLocation, IResponseModel } from '@core/interface';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { GoogleMapsApiService } from '../google/google-maps-api.service';
import { DealerHelperService } from './dealer-helper.service';
import { IPDLScheduleHours } from '../../interfaces/dealer/pdl-schedule-hours.model';

@Injectable({
  providedIn: 'root',
})
export class DealerService extends DealerHelperService {
  readonly #http = inject(HttpClient);
  private readonly googleMapsApiService = inject(GoogleMapsApiService);
  private readonly dealerByIdSignal = signal<IDealerByLocation | null>(null);

  async fetchDealerDetails(
    id: string,
    country: string,
    modelGroup = ''
  ): Promise<void> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/dealers/${id}?country=${country}&modelGroup=${modelGroup}`;

    await firstValueFrom(
      this.#http.get<IResponseModel<IDealerByLocation>>(url).pipe(
        tap((data) => this.setAndBuildDealer(data.response))
      )
    );
  }

  /**
   * set o dealer atual.
   * @param {IDealerByLocation} dealer - O dealer a ser definido.
   * @returns {void}
   *
   * Esta função define o dealer atual na instância do serviço.
   * Ela armazena o dealer passado como parâmetro na propriedade `dealerSignal`
   * da classe, permitindo que outras partes da aplicação acessem
   * as informações do dealer atual.
   * Além disso, a função salva o dealer selecionado no sessionStorage
   * para persistência entre sessões (sessionStorage).
   * Também chama métodos auxiliares para formatar os horários do dealer
   * e filtrar os serviços de experiência remota disponíveis.
   *
   * @optional origin - Coordenadas de origem para cálculo de distância (latitude e longitude).
   * @returns {Promise<void>} - Uma Promise que resolve quando o dealer é definido e processado.
   */
  async setAndBuildDealer(dealer: IDealerByLocation | null, origin?: { lat: number; lng: number }): Promise<void> {
    let formattedDealer: IDealerByLocation | null = null;
    if (dealer) {
      dealer.selected = true;
      formattedDealer = this.buildFormattedDealer(dealer);

      if (origin) {
        const destination: { lat: number; lng: number } =
        { lat: dealer.latitude, lng: dealer.longitude };
        const data = await firstValueFrom(
          this.googleMapsApiService.getDistanceBetweenPoints(origin, destination)
        );
        formattedDealer.distance = Math.min(...data.routes.map(route => route.distanceMeters));
      }
    }

    this.dealerByIdSignal.set(formattedDealer);
    this.saveDealerId(formattedDealer?.dealerId.toString() ?? '');
    sessionStorage.setItem(StorageKeys.DEALER_SELECTED, JSON.stringify(formattedDealer));
  }

  getSelectedDealer() {
    const dealerStorage = sessionStorage.getItem(StorageKeys.DEALER_SELECTED);
    if (!dealerStorage) return null;

    const dealer: IDealerByLocation = JSON.parse(dealerStorage);

    if (dealer) {
      return dealer;
    }

    return this.dealerByIdSignal();
  }

  /**
  * Verifica se o dealer possui um serviço específico de localizador de concessionárias.
  *
  * @param {string} serviceName - O nome do serviço a ser verificado.
   */
  hasDealerLocatorService(serviceName: string): boolean {
    const dealer = this.getSelectedDealer();
    return this.checkDealerHasLocatorService(dealer, serviceName);
  }

  /**
   * Verifica se o dealer atualmente selecionado oferece serviços específicos para veículos elétricos.
   *
   * Procura através do array `dealerLocatorServices` do dealer selecionado por serviços
   * que contenham "Electric" (case-insensitive).
   *
   * @returns `true` se o dealer suporta serviços elétricos, `false` se nenhum dealer está selecionado ou não suporta serviços elétricos
   *
   * @example
   * ```typescript
   * if (dealerService.hasElectricalServiceSupport()) {
   *   // Permitir acesso à experiência de agendamento de veículos elétricos
   * }
   * ```
   */
  hasElectricalServiceSupport(): boolean {
    const dealer = this.getSelectedDealer();
    if (!dealer) return false;
    return this.dealerHasElectricalService(dealer);
  }

  /**
   * Verifica se o dealer atualmente selecionado oferece serviços específicos para Mustang.
   *
   * Procura através do array `dealerLocatorServices` do dealer selecionado por serviços
   * que contenham "Mustang" (case-insensitive).
   *
   * @returns `true` se o dealer suporta serviços Mustang, `false` se nenhum dealer está selecionado ou não suporta serviços Mustang
   *
   * @example
   * ```typescript
   * if (dealerService.hasMustangServiceSupport()) {
   *   // Permitir acesso à experiência de agendamento específica para Mustang
   * }
   * ```
   */
  hasMustangServiceSupport(): boolean {
    const dealer = this.getSelectedDealer();
    console.log('Selected Dealer:', dealer);
    if (!dealer) return false;
    return this.dealerHasMustangService(dealer.dealerLocatorServices || []);
  }

  getPickUpAndDeliveryScheduleHours(date: string, dealerId: number, country: string): Observable<IPDLScheduleHours | null> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/services/pickup-delivery/hours?country=${country}&date=${date}&id=${dealerId}`;

    return this.#http.get<IPDLScheduleHours | null>(url);
  }

}
